import React, { useEffect, useState, useCallback } from "react";
import { Navbar, MobileNav } from "@/components/Navbar";
import { api } from "@/lib/api";
import { CreatorCard } from "@/components/CreatorCard";
import { ConnectButton } from "@/components/ConnectButton";
import { InviteCreatorButton } from "@/components/InviteCreatorButton";
import { PageMeta } from "@/components/PageMeta";
import { Users, UserCheck, Sparkles, Loader2, Gift } from "lucide-react";

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-zinc-200 bg-white p-4">
      <div className="flex items-center gap-2 text-zinc-500">
        <Icon className="h-4 w-4" />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <p className="mt-1 font-display text-3xl font-bold text-zinc-900">{value}</p>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="mt-8">
      <h2 className="font-display text-lg font-bold text-zinc-900">{title}</h2>
      {subtitle && <p className="mt-0.5 text-sm text-zinc-500">{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function suggestNote(c) {
  const bits = [];
  if (c.mutual_count > 0) bits.push(`${c.mutual_count} mutual connection${c.mutual_count !== 1 ? "s" : ""}`);
  if (c.same_location && c.location) bits.push(c.location);
  else if ((c.shared_niches || []).length > 0) bits.push((c.shared_niches || []).slice(0, 2).join(", "));
  return bits.join(" · ");
}

function mutualNote(c) {
  if (c.via_name) {
    const others = (c.mutual_count || 1) - 1;
    return others > 0 ? `Connected with ${c.via_name} +${others} more` : `Connected with ${c.via_name}`;
  }
  return `${c.mutual_count} mutual connection${c.mutual_count !== 1 ? "s" : ""}`;
}

export default function MyNetwork() {
  const [data, setData] = useState({ connections: [], incoming: [], outgoing: [], counts: { connections: 0, incoming: 0, outgoing: 0 } });
  const [suggested, setSuggested] = useState([]);
  const [mutualSuggested, setMutualSuggested] = useState([]);
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [net, sug, mut, inv] = await Promise.all([
      api.get("/connections/me"),
      api.get("/connections/suggested"),
      api.get("/connections/network-suggestions").catch(() => ({ data: { suggestions: [] } })),
      api.get("/invites/me").catch(() => ({ data: null })),
    ]);
    setData(net.data);
    setSuggested(sug.data.suggested || []);
    setMutualSuggested(mut.data.suggestions || []);
    setInvite(inv.data);
  }, []);

  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);

  const refresh = () => load();

  return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-nav-10">
      <PageMeta title="My Network · Nearfluence" description="Your creator connections on Nearfluence." />
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2">
          <Users className="h-6 w-6 text-emerald-600" />
          <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900">My Network</h1>
        </div>
        <p className="mt-1 text-sm text-zinc-500">Build your professional creator network on Nearfluence.</p>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <StatCard testid="stat-connections" icon={Users} label="Connections" value={data.counts.connections} />
              <StatCard testid="stat-incoming" icon={UserCheck} label="Requests" value={data.counts.incoming} />
              <StatCard testid="stat-suggested" icon={Sparkles} label="Suggested" value={suggested.length} />
            </div>

            {/* Invite a Creator */}
            <div data-testid="invite-card" className="mt-6 overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-900 p-5 text-white sm:flex sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/20 text-emerald-400"><Gift className="h-5 w-5" /></div>
                <div>
                  <h3 className="font-display font-bold">Invite a Creator</h3>
                  <p className="mt-0.5 text-sm text-zinc-300">Grow the community — invite creators you know to join your network on Nearfluence.</p>
                  {invite && (
                    <div data-testid="founding-progress" className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                      <span className="rounded-full bg-white/10 px-2.5 py-0.5 font-semibold text-emerald-300">
                        {invite.successful_invites} successful invite{invite.successful_invites !== 1 ? "s" : ""}
                      </span>
                      {(() => {
                        const tiers = [1, 3, 5, 10];
                        const next = tiers.find((t) => t > invite.successful_invites);
                        return next ? <span className="text-zinc-400">Next milestone: {next} — Founding Creator perks coming soon</span> : <span className="text-emerald-300">🎉 Founding Creator status reached</span>;
                      })()}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 sm:mt-0 sm:pl-4">
                <InviteCreatorButton className="bg-emerald-500 text-white hover:bg-emerald-600" />
              </div>
            </div>

            {/* Growth prompt */}
            <div data-testid="growth-prompt" className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5">
              <h3 className="font-display font-bold text-zinc-900">Grow your creator network</h3>
              <p className="mt-1 text-sm text-zinc-600">Know creators who should be on Nearfluence? Connecting with more creators helps you discover collaborations.</p>
            </div>

            {data.incoming.length > 0 && (
              <Section title="Connection requests" subtitle="Creators who want to connect with you.">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {data.incoming.map((c, i) => (
                    <CreatorCard key={c.user_id} creator={c} index={i}
                      footer={<ConnectButton userId={c.user_id} creatorName={c.display_name}
                        initial={{ status: "incoming", connection_id: c.connection_id }} onChange={refresh} />} />
                  ))}
                </div>
              </Section>
            )}

            <Section title="Your connections" subtitle={data.connections.length ? "Creators in your network." : "You haven't connected with anyone yet."}>
              {data.connections.length > 0 ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {data.connections.map((c, i) => (
                    <CreatorCard key={c.user_id} creator={c} index={i}
                      footer={<ConnectButton userId={c.user_id} creatorName={c.display_name}
                        initial={{ status: "connected", connection_id: c.connection_id }} onChange={refresh} className="w-full" />} />
                  ))}
                </div>
              ) : (
                <p className="rounded-2xl border border-dashed border-zinc-300 bg-white px-4 py-8 text-center text-sm text-zinc-400" data-testid="no-connections">
                  Start connecting with creators from the suggestions below.
                </p>
              )}
            </Section>

            {data.outgoing.length > 0 && (
              <Section title="Pending requests you sent">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {data.outgoing.map((c, i) => (
                    <CreatorCard key={c.user_id} creator={c} index={i}
                      footer={<ConnectButton userId={c.user_id} creatorName={c.display_name}
                        initial={{ status: "requested", connection_id: c.connection_id }} onChange={refresh} className="w-full" />} />
                  ))}
                </div>
              </Section>
            )}

            {mutualSuggested.length > 0 && (
              <Section title="Creators your connections know" subtitle="People near you who your connections also connected with.">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {mutualSuggested.map((c, i) => (
                    <CreatorCard key={c.user_id} creator={c} index={i} note={mutualNote(c)}
                      footer={<ConnectButton userId={c.user_id} creatorName={c.display_name}
                        initial={{ status: "none", connection_id: null }} onChange={refresh} className="w-full" />} />
                  ))}
                </div>
              </Section>
            )}

            <Section title="Creators you may want to connect with" subtitle="Suggested from your location, niche and mutual connections.">
              {suggested.length > 0 ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {suggested.map((c, i) => (
                    <CreatorCard key={c.user_id} creator={c} index={i} note={suggestNote(c)}
                      footer={<ConnectButton userId={c.user_id} creatorName={c.display_name}
                        initial={{ status: "none", connection_id: null }} onChange={refresh} className="w-full" />} />
                  ))}
                </div>
              ) : (
                <p className="rounded-2xl border border-dashed border-zinc-300 bg-white px-4 py-8 text-center text-sm text-zinc-400" data-testid="no-suggestions">
                  No suggestions right now — check back as more creators join.
                </p>
              )}
            </Section>
          </>
        )}
      </div>
      <MobileNav />
    </div>
  );
}
